import {useMemo} from 'react'
import {useFormContext, useWatch} from 'react-hook-form'
import {MedicalReportValidationSchema} from '@/features/medical-reports/formSchemas'
import {useTranslation} from '@/translations/i18n'
import {FormSection} from '@/components/commons/form-section/FormSection'
import {ObjectKeys} from '@/utilities/helpers'
import {isSingleNoteReport} from '../../utils'
import {MedicalReportView} from '../../types'
import {MedicalReportDateSection} from '../medical-report-date-section/MedicalReportDateSection'
import {MedicalReportPatientSection} from '../medical-report-patient-section/MedicalReportPatientSection'
import {MedicalReportTypeAndAccessSection} from '../medical-report-type-section/MedicalReportTypeSection'
import {MedicalReportProjectSection} from '../medical-report-project-section/MedicalReportProjectSection'
import {MedicalReportNoteSection} from '../medical-report-note-section/MedicalReportNoteSection'
import {StartContinuousProjectSections} from '../start-continuous-project-sections/StartContinuousProjectSections'
import {CloseContinuousProjectSections} from '../close-continuous-project-sections/CloseContinuousProjectSections'
import {RenewContinuousProjectSections} from '../renew-continuous-project-sections/RenewContinuousProjectSections'
import {StartCyclicalProjectSections} from '../start-cyclical-project-sections/StartCyclicalProjectSections'
import {CloseCyclicalProjectSections} from '../close-cyclical-project-sections/CloseCyclicalProjectSections'
import {ExtensionRequestSections} from '../extension-request-sections/ExtensionRequestSections'
import {MedicalReportUneligibleType} from '../medical-report-uneligible-type/MedicalReportUneligibleType'
import {InitialSocialServiceEvaluationSections} from '../initial-social-service-evaluation-sections/InitialSocialServiceEvaluationSections'
import {TeamReportSections} from '../team-report-sections/TeamReportSections'
import {DocumentSections} from '../document-sections/DocumentSections'

export const MedicalReportFields = ({
    view,
    isTypeProjectCompliant,
    status
}: {
    view: MedicalReportView
    isTypeProjectCompliant: boolean
    status?: 'draft' | 'signed'
}) => {
    const {t} = useTranslation()

    const form = useFormContext<MedicalReportValidationSchema>()

    const reportType = useWatch({control: form.control, name: 'reportType'})
    const project = useWatch({control: form.control, name: 'project'})

    const isProjectAvailable = useMemo(() => !!project && !!ObjectKeys(project).length, [project])

    return (
        <form onSubmit={e => e.preventDefault()}>
            <MedicalReportDateSection />
            <MedicalReportPatientSection />
            <MedicalReportTypeAndAccessSection isDisabled={view == 'edit' && status !== 'draft'} />
            {reportType?.keyName &&
                (isTypeProjectCompliant ? (
                    <>
                        <MedicalReportProjectSection isDisabled={view == 'edit'} />
                        {isSingleNoteReport(reportType.groupKeyName, reportType.keyName) ? (
                            <MedicalReportNoteSection />
                        ) : (
                            <>
                                {reportType.keyName == 'start_continuous_project' && <StartContinuousProjectSections />}
                                {reportType.keyName == 'close_continuous_project' && <CloseContinuousProjectSections />}
                                {reportType.keyName == 'renew_continuous_project' && <RenewContinuousProjectSections />}
                                {reportType.keyName == 'start_cyclical_project' && <StartCyclicalProjectSections />}
                                {reportType.keyName == 'close_cyclical_project' && <CloseCyclicalProjectSections />}
                                {reportType.keyName == 'continuous_project_extension_request' && project && (
                                    <ExtensionRequestSections />
                                )}
                                {reportType.keyName == 'initial_social_service_evaluation' && (
                                    <InitialSocialServiceEvaluationSections />
                                )}
                                {(reportType.keyName == 'team_report' || reportType.keyName == 'glo') && (
                                    <TeamReportSections />
                                )}
                                {reportType.keyName == 'document' && <DocumentSections />}
                            </>
                        )}
                    </>
                ) : (
                    <FormSection title={t('commons:reference_project')}>
                        <MedicalReportUneligibleType isProjectAvailable={isProjectAvailable} />
                    </FormSection>
                ))}
        </form>
    )
}
